import React, { useState } from "react";
import payrollService from "../../services/payrollService";
import Notification from "../../components/UI/Notification";
import ExportButton from "../../components/Payroll/ExportButton";
import { Search, Clock, User, FileText } from "lucide-react";

const ApprovalHistoryPage = () => {
  const [payrollId, setPayrollId] = useState("");
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const fetchHistory = async () => {
    if (!payrollId.trim()) {
      setNotification({
        type: "warning",
        message: "Please enter a payroll ID",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await payrollService.getApprovalHistory(payrollId);
      if (response.success) {
        setHistory(response.data);
      }
    } catch (error) {
      console.error("Error fetching approval history:", error);
      setNotification({
        type: "error",
        message: "Failed to fetch approval history",
      });
      setHistory(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setNotification({
      type: "info",
      message: `Exporting to ${format.toUpperCase()}...`,
    });
    // Implementation for export
  };

  const getActionColor = (action) => {
    const colors = {
      submitted: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
      approved: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
      rejected: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
      cancelled: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
    };
    return colors[action] || colors.submitted;
  };

  return (
    <div className="p-6 font-poppins">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Approval History
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            View payroll approval timeline and actions
          </p>
        </div>
        {history && (
          <ExportButton onExport={handleExport} formats={["pdf", "excel"]} />
        )}
      </div>

      {/* Search Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={payrollId}
              onChange={(e) => setPayrollId(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && fetchHistory()}
              placeholder="Enter Payroll ID..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
          <button
            onClick={fetchHistory}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors disabled:opacity-50"
          >
            {loading ? "Searching..." : "Search"}
          </button>
        </div>
      </div>

      {/* History Display */}
      {history && (
        <>
          {/* Payroll Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Payroll Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Employee ID</p>
                <p className="text-base font-semibold text-gray-900 dark:text-white">
                  {history.payroll.employee_id}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Period</p>
                <p className="text-base font-semibold text-gray-900 dark:text-white">
                  {history.payroll.payroll_year}-{String(history.payroll.payroll_month).padStart(2, "0")}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Net Salary</p>
                <p className="text-base font-semibold text-green-600 dark:text-green-400">
                  ₹{parseFloat(history.payroll.net_salary).toLocaleString("en-IN")}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getActionColor(history.payroll.status)}`}>
                  {history.payroll.status}
                </span>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              Approval Timeline
            </h3>
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-300 dark:bg-gray-600"></div>

              {/* Timeline Items */}
              <div className="space-y-6">
                {history.approvals && history.approvals.length > 0 ? (
                  history.approvals.map((approval, index) => (
                    <div key={approval.id} className="relative flex items-start">
                      {/* Timeline Dot */}
                      <div className="absolute left-6 w-4 h-4 rounded-full bg-blue-600 border-4 border-white dark:border-gray-800"></div>

                      {/* Content */}
                      <div className="ml-16 flex-1">
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getActionColor(approval.action)}`}>
                                {approval.action}
                              </span>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                by {approval.approver?.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                              <Clock size={14} />
                              {approval.created_at}
                            </div>
                          </div>
                          {approval.remarks && (
                            <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                              <FileText size={14} className="inline mr-1" />
                              <span className="font-medium">Remarks:</span> {approval.remarks}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                    No approval history found
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Empty State */}
      {!history && !loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
              <FileText size={32} className="text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No History Selected
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Enter a payroll ID to view its approval history
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalHistoryPage;

